import axios from "axios";

const normalizeBaseURL = (rawUrl = "") => {
  const cleaned = rawUrl.trim().replace(/\/+$/, "");

  if (!cleaned) {
    return "";
  }

  return cleaned.replace(/\/api$/i, "");
};

const resolveBaseURL = () => {
  const envBaseURL = normalizeBaseURL(process.env.REACT_APP_API_URL || "");

  if (typeof window !== "undefined") {
    const { hostname } = window.location;

    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return envBaseURL || "http://localhost:3000";
    }

    return envBaseURL;
  }

  return envBaseURL;
};

const api = axios.create({
  baseURL: resolveBaseURL(),
});

// 1️⃣ Interceptor de request (token automático)
api.interceptors.request.use((config) => {
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");
  const storedWorkspace = String(localStorage.getItem("activeWorkspace") || "").trim();
  const workspace = /^business(?::[a-f\d]{24})?$/i.test(storedWorkspace)
    ? storedWorkspace
    : "personal";

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers["X-Workspace"] = workspace;

  return config;
});

// 2️⃣ Interceptor de respuesta (si expira token)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const requestUrl = error.config?.url || "";
    const isAuthRequest =
      requestUrl.includes("/api/auth/login") ||
      requestUrl.includes("/api/auth/signup");

    if (error.response?.status === 401 && !isAuthRequest) {
      const hadToken = Boolean(
        localStorage.getItem("token") || sessionStorage.getItem("token")
      );
      localStorage.removeItem("token");
      sessionStorage.removeItem("token");
      // Solo redirigimos (con aviso) si había una sesión activa que expiró.
      if (hadToken && typeof window !== "undefined") {
        window.location.href = "/login?expired=1";
      }
    }
    return Promise.reject(error);
  }
);

// Servicios
export const taskService = {
  getAll: (params = {}) => api.get("/api/task", { params }),
  getByDate: (fecha, params = {}) => api.get("/api/task", { params: { fecha, ...params } }),
  updateStatus: (taskId, data) =>
    api.put(`/api/task/${taskId}/status`, data),
  delete: (taskId) => api.delete(`/api/task/${taskId}`),
  // 🧹 Borrar todas las tareas (empezar de cero)
  deleteAll: () => api.delete("/api/task/all", { data: { confirmar: "BORRAR" } }),
  create: (data) => api.post("/api/task", data),
  // 🎨 Prioridades personalizadas (nombre + color)
  getPrioridades: () => api.get("/api/task/prioridades"),
  savePrioridades: (prioridades) => api.put("/api/task/prioridades", { prioridades }),
  // 🏷️ Etiquetas de notas (nombre + color)
  getEtiquetasNotas: () => api.get("/api/task/etiquetas-notas"),
  saveEtiquetasNotas: (etiquetas) => api.put("/api/task/etiquetas-notas", { etiquetas }),
  update: (taskId, data) => api.put(`/api/task/${taskId}`, data),
  // 🖼️ Imagen de nota (data URI) → URL en Vercel Blob
  subirImagen: (data) => api.post("/api/task/imagen", data),
  // 👥 Compartir tareas
  buscarUsuario: (u) => api.get("/api/task/buscar-usuario", { params: { u } }),
  compartir: (taskId, data) => api.post(`/api/task/${taskId}/compartir`, data),
  invitaciones: () => api.get("/api/task/invitaciones"),
  aceptarInvitacion: (taskId) => api.post(`/api/task/${taskId}/aceptar`),
  salir: (taskId) => api.post(`/api/task/${taskId}/salir`),
  quitarColaborador: (taskId, userId) => api.delete(`/api/task/${taskId}/colaborador/${userId}`),
};

export const movimientoService = {
  getAll: (params = {}) => api.get("/api/add", { params }),
  create: (data) => api.post("/api/add", data),
  update: (id, data) => api.put(`/api/add/${id}`, data),
  settleDebt: (id, data) => api.post(`/api/add/${id}/settle-debt`, data),
  emitirFactura: (id) => api.post(`/api/add/${id}/factura`),
  delete: (id) => api.delete(`/api/add/${id}`),
  // 🧹 Borrar todos los movimientos y categorías (empezar de cero)
  deleteAll: () => api.delete("/api/add/all", { data: { confirmar: "BORRAR" } }),
};

export const categoriesService = {
  getAll: () => api.get("/api/categories"),
  create: (data) => api.post("/api/categories", data),
  delete: (id) => api.delete(`/api/categories/${id}`),
};

export const sharedGroupsService = {
  getAll: () => api.get("/api/shared-groups"),
  getById: (id) => api.get(`/api/shared-groups/${id}`),
  create: (data) => api.post("/api/shared-groups", data),
  update: (id, data) => api.put(`/api/shared-groups/${id}`, data),
  addMember: (id, data) => api.post(`/api/shared-groups/${id}/members`, data),
  delete: (id) => api.delete(`/api/shared-groups/${id}`),
  createExpense: (id, data) => api.post(`/api/shared-groups/${id}/expenses`, data),
  createDebt: (id, data) => api.post(`/api/shared-groups/${id}/debts`, data),
  settleDebt: (groupId, debtId, data) =>
    api.post(`/api/shared-groups/${groupId}/debts/${debtId}/settle`, data),
  deleteExpense: (groupId, expenseId) =>
    api.delete(`/api/shared-groups/${groupId}/expenses/${expenseId}`),
};

export const googleService = {
  // Estado de la conexión con Google Calendar del usuario logueado
  getStatus: () => api.get("/api/google/status"),
  // Devuelve { url } para mandar al usuario al consentimiento de Google
  getAuthUrl: () => api.get("/api/google/auth"),
  // Trae los eventos de Google y los refleja como tareas (Calendar → Web)
  sync: () => api.post("/api/google/sync"),
  disconnect: () => api.post("/api/google/disconnect"),
};

// Config de facturación electrónica (ARCA) del perfil activo.
// El workspace viaja automático en el header X-Workspace del interceptor.
export const fiscalService = {
  get: () => api.get("/api/fiscal-config"),
  update: (data) => api.put("/api/fiscal-config", data),
};

export const timeEntryService = {
  getAll: (params) => api.get("/api/time-entries", { params }),
  create: (data) => api.post("/api/time-entries", data),
  update: (id, data) => api.put(`/api/time-entries/${id}`, data),
  delete: (id) => api.delete(`/api/time-entries/${id}`),
};

export const projectService = {
  getAll: () => api.get("/api/projects"),
  create: (data) => api.post("/api/projects", data),
  update: (id, data) => api.put(`/api/projects/${id}`, data),
  delete: (id) => api.delete(`/api/projects/${id}`),
};

export const metaService = {
  getAll: () => api.get("/api/metas"),
  create: (data) => api.post("/api/metas", data),
  update: (id, data) => api.put(`/api/metas/${id}`, data),
  delete: (id) => api.delete(`/api/metas/${id}`),
};

export const journalService = {
  // `fecha` es la fecha local del cliente (YYYY-MM-DD).
  get: (fecha) => api.get(`/api/journal?fecha=${fecha}`),
  save: (data) => api.put("/api/journal", data),
  savePreguntas: (data) => api.put("/api/journal/preguntas", data),
};

export const saludService = {
  get: () => api.get("/api/salud"),
  // Manda solo las secciones a actualizar; el backend mergea por día.
  update: (data) => api.put("/api/salud", data),
  // Caminatas con su trazado GPS (aparte, para el visor de recorridos).
  recorridos: () => api.get("/api/salud/recorridos"),
  // Borra una caminata puntual (por fecha + metros + secs).
  borrarRecorrido: (data) => api.delete("/api/salud/recorridos", { data }),
};

export const gymService = {
  get: () => api.get("/api/gym"),
  // Manda solo lo que cambió (entrenos por día, rutinas o ejercicios).
  update: (data) => api.put("/api/gym", data),
};

// Comunidad (Fase 1: perfil público, seguir, posteos, feed).
export const communityService = {
  getMiPerfil: () => api.get("/api/community/me"),
  updateMiPerfil: (data) => api.put("/api/community/me", data),
  getPerfil: (username) => api.get(`/api/community/users/${username}`),
  buscar: (q) => api.get("/api/community/buscar", { params: { q } }),
  seguir: (userId) => api.post(`/api/community/follow/${userId}`),
  dejarDeSeguir: (userId) => api.delete(`/api/community/follow/${userId}`),
  aceptarSolicitud: (userId) => api.post(`/api/community/solicitudes/${userId}/aceptar`),
  rechazarSolicitud: (userId) => api.post(`/api/community/solicitudes/${userId}/rechazar`),
  notificaciones: () => api.get("/api/community/notificaciones"),
  marcarLeidas: () => api.post("/api/community/notificaciones/leer"),
  // Chat
  conversaciones: () => api.get("/api/community/chat/conversaciones"),
  chatNoLeidos: () => api.get("/api/community/chat/no-leidos"),
  getDM: (userId) => api.get(`/api/community/chat/dm/${userId}`),
  enviarDM: (userId, texto) => api.post(`/api/community/chat/dm/${userId}`, { texto }),
  getChatGrupo: (grupoId) => api.get(`/api/community/chat/grupo/${grupoId}`),
  enviarChatGrupo: (grupoId, texto) => api.post(`/api/community/chat/grupo/${grupoId}`, { texto }),
  seguidores: (userId) => api.get(`/api/community/users/${userId}/followers`),
  siguiendo: (userId) => api.get(`/api/community/users/${userId}/following`),
  crearPost: (data) => api.post("/api/community/posts", data),
  feed: (params) => api.get("/api/community/feed", { params }),
  postsDeUsuario: (userId) => api.get(`/api/community/users/${userId}/posts`),
  postsDeGrupo: (groupId) => api.get(`/api/community/grupos/${groupId}/posts`),
  kudos: (postId) => api.post(`/api/community/posts/${postId}/kudos`),
  borrarPost: (postId) => api.delete(`/api/community/posts/${postId}`),
  comentarios: (postId) => api.get(`/api/community/posts/${postId}/comentarios`),
  comentar: (postId, texto) => api.post(`/api/community/posts/${postId}/comentarios`, { texto }),
  borrarComentario: (id) => api.delete(`/api/community/comentarios/${id}`),
};

// Clubes / grupos (Fase 3).
export const gruposService = {
  crear: (data) => api.post("/api/community/grupos", data),
  descubrir: (q) => api.get("/api/community/grupos", { params: { q } }),
  mios: () => api.get("/api/community/grupos/mios"),
  get: (id) => api.get(`/api/community/grupos/${id}`),
  unirse: (id) => api.post(`/api/community/grupos/${id}/join`),
  salir: (id) => api.delete(`/api/community/grupos/${id}/join`),
  miembros: (id) => api.get(`/api/community/grupos/${id}/miembros`),
  editar: (id, data) => api.put(`/api/community/grupos/${id}`, data),
  borrar: (id) => api.delete(`/api/community/grupos/${id}`),
};

// Retos / desafíos (Fase 3).
export const retosService = {
  crear: (data) => api.post("/api/community/retos", data),
  descubrir: (q) => api.get("/api/community/retos", { params: { q } }),
  mios: () => api.get("/api/community/retos/mios"),
  get: (id) => api.get(`/api/community/retos/${id}`),
  unirse: (id) => api.post(`/api/community/retos/${id}/join`),
  salir: (id) => api.delete(`/api/community/retos/${id}/join`),
  ranking: (id) => api.get(`/api/community/retos/${id}/ranking`),
  editar: (id, data) => api.put(`/api/community/retos/${id}`, data),
  borrar: (id) => api.delete(`/api/community/retos/${id}`),
};

export const afirmacionService = {
  // `fecha` es la fecha local del cliente (YYYY-MM-DD): el "hoy" lo define el
  // dispositivo, no el servidor.
  get: (fecha) => api.get(`/api/afirmaciones?fecha=${fecha}`),
  save: (data) => api.put("/api/afirmaciones", data),
  marcarLeido: (fecha) => api.post("/api/afirmaciones/leer", { fecha }),
  desmarcarLeido: (fecha) => api.delete(`/api/afirmaciones/leer?fecha=${fecha}`),
};

export const authService = {
  login: (credentials) =>
    api.post("/api/auth/login", credentials),
  signup: (userData) =>
    api.post("/api/auth/signup", userData),
  forgotPassword: (data) => api.post("/api/auth/forgot-password", data),
  resetPassword: (data) => api.post("/api/auth/reset-password", data),
  changePassword: (data) => api.post("/api/auth/change-password", data),
  getProfile: () => api.get("/api/auth/profile"),
  updateProfile: (data) => api.put("/api/auth/profile", data),
  checkUsername: (u) => api.get("/api/auth/username-available", { params: { u } }),
};

// Panel /monitoreo — solo responde para emails en ADMIN_EMAILS (backend)
export const adminService = {
  overview: () => api.get("/api/admin/overview"),
  health: () => api.get("/api/admin/health"),
  security: () => api.get("/api/admin/security"),
  appUpdate: () => api.get("/api/admin/app-update"),
  saveAppUpdate: (data) => api.put("/api/admin/app-update", data),
};

export default api;
